// Utilidades para el manejo correcto de fechas y horas en horario español
// Este módulo resuelve los problemas de conversión de zona horaria entre
// el backend (que envía fechas UTC) y el frontend (que debe mostrarlas en horario español)

/**
 * Convierte una fecha UTC del backend a fecha local española
 * Trata los componentes UTC como si fueran componentes locales españoles
 * 
 * @param utcDateString - Fecha en formato ISO UTC del backend (ej: "2025-10-14T18:45:00.000000Z")
 * @returns Date object representando la hora local española
 */
export function parseSpanishDateTime(utcDateString: string): Date {
  const utcDate = new Date(utcDateString);
  
  // Extraer componentes UTC y tratarlos como componentes locales españoles
  return new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
    utcDate.getUTCHours(),
    utcDate.getUTCMinutes(),
    utcDate.getUTCSeconds()
  );
}

/**
 * Convierte una fecha local española a formato UTC para enviar al backend
 * Asegura que la hora local se mantenga igual en la base de datos
 * 
 * @param localDate - Date object con fecha y hora local española
 * @returns String en formato ISO UTC para el backend
 */
export function formatSpanishDateTimeForBackend(localDate: Date): string {
  const year = localDate.getFullYear().toString().padStart(4, '0');
  const month = (localDate.getMonth() + 1).toString().padStart(2, '0');
  const day = localDate.getDate().toString().padStart(2, '0');
  const hours = localDate.getHours().toString().padStart(2, '0');
  const minutes = localDate.getMinutes().toString().padStart(2, '0');
  const seconds = localDate.getSeconds().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
}

/**
 * Crea una fecha local española a partir de componentes de fecha y hora
 * 
 * @param year - Año
 * @param month - Mes (1-12)
 * @param day - Día del mes
 * @param hours - Hora (0-23)
 * @param minutes - Minutos (0-59)
 * @returns Date object con la fecha y hora especificadas
 */
export function createSpanishDateTime(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number
): Date {
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

/**
 * Formatea una fecha como string de hora para mostrar en la interfaz
 * 
 * @param date - Date object
 * @returns String en formato HH:MM
 */
export function formatTimeForDisplay(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Parsea un string de hora en formato HH:MM
 * 
 * @param timeString - String en formato HH:MM
 * @returns Objeto con horas y minutos como números
 */
export function parseTimeString(timeString: string): { hours: number; minutes: number } {
  const [hours, minutes] = timeString.split(':').map(Number);
  return { hours, minutes };
}
